import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as firebaseSignOut, setPersistence, indexedDBLocalPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { useToast } from '@/components/ui/use-toast';
import { processReferral } from '@/lib/referralSystem';
import { debugLog } from '@/components/DebugConsole';
import { isQrPromoReservedSlug, QR_SLUG_RESERVED_MESSAGE } from '@/lib/reservedUrlSlug';

const AuthContext = createContext(undefined);

// Utility function to detect account type (can be reused)
const detectAccountType = (artistName, email, signupPath, isAgent) => {
  if (isAgent) return 'agent';
  if (signupPath && signupPath.includes('/karaoke')) return 'karaoke';
  if (artistName || email) {
    const combinedText = `${artistName || ''} ${email || ''}`.toLowerCase();
    const karaokeIndicators = ['karaoke', 'dj', 'host', 'mc', 'emcee', 'venue', 'bar', 'club', 'pub'];
    if (karaokeIndicators.some(indicator => combinedText.includes(indicator))) {
      return 'karaoke';
    }
  }
  return 'artist';
};

export const AuthProvider = ({ children }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Simple setUser function
  const setUserState = useCallback((userData) => {
    setUser(userData);
  }, []);

  const buildFallbackProfile = useCallback((authUser) => {
    const email = authUser?.email || '';
    const emailPrefix = email.includes('@') ? email.split('@')[0] : '';
    return {
      artist_name: authUser?.displayName || emailPrefix || 'Artist',
      account_type: 'artist',
      email: email || null,
      currency: 'GBP',
      country: 'GB',
      profile_recovered: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }, []);

  const fetchProfile = useCallback(async (userId, authUser = null) => {
    if (!db) {
      console.warn('⚠️ Firestore not available, skipping profile fetch');
      return authUser ? buildFallbackProfile(authUser) : null;
    }

    console.log('🔍 Fetching Firestore profile for user:', userId);
    try {
      const profileDocRef = doc(db, 'profiles', userId);
      const docSnap = await getDoc(profileDocRef);

      if (docSnap.exists()) {
        console.log('✅ Firestore profile fetched successfully:', docSnap.data());
        return docSnap.data();
      } else {
        console.error('❌ No such profile document in Firestore! Attempting profile recovery.');
        const recoveredProfile = buildFallbackProfile(authUser || null);
        if (recoveredProfile) {
          try {
            await setDoc(profileDocRef, recoveredProfile, { merge: true });
            console.log('🛠️ Recovered missing profile document successfully');
            return recoveredProfile;
          } catch (recoveryError) {
            console.error('❌ Profile recovery write failed:', recoveryError);
            return recoveredProfile;
          }
        }
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      return authUser ? buildFallbackProfile(authUser) : null;
    }
  }, [buildFallbackProfile]);

  useEffect(() => {
    if (!auth) {
      console.warn('⚠️ Firebase auth not available, skipping auth state listener');
      setLoading(false);
      setAuthInitialized(true);
      return;
    }

    // Set up Firebase Auth persistence
    const setupPersistence = async () => {
      if (!auth) {
        console.warn('⚠️ Auth not available for persistence setup');
        return;
      }
      
      try {
        // Try indexedDB first for better compatibility
        await setPersistence(auth, indexedDBLocalPersistence);
        console.log('✅ Firebase Auth persistence set to indexedDBLocalPersistence');
      } catch (error) {
        console.warn('⚠️ indexedDB persistence failed, trying browser persistence:', error.message);
        // Fallback to browser persistence if indexedDB fails
        try {
          await setPersistence(auth, browserLocalPersistence);
          console.log('✅ Fallback: Firebase Auth persistence set to browserLocalPersistence');
        } catch (fallbackError) {
          console.error('❌ Fallback persistence also failed:', fallbackError);
          // Don't throw - auth will still work without explicit persistence
          console.warn('⚠️ Continuing without explicit persistence - Firebase will use default');
        }
      }
    };

    // Initialize auth with proper persistence
    const initializeAuth = async () => {
      // Set up persistence first
      await setupPersistence();
      
      const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
        if (authUser) {
          console.log('✅ Firebase Auth: User authenticated:', authUser.uid);
          console.log('🔍 DEBUG: Auth user object:', authUser);
          console.log('🔍 DEBUG: Auth user getIdToken available:', typeof authUser.getIdToken);
          
          // Test getting the ID token to ensure it works
          try {
            const idToken = await authUser.getIdToken();
            console.log('🔍 DEBUG: ID token obtained successfully:', idToken ? 'Present' : 'Missing');
            console.log('🔍 DEBUG: ID token length:', idToken ? idToken.length : 0);
          } catch (tokenError) {
            console.error('❌ Error getting ID token:', tokenError);
          }
          
          setUserState(authUser);
          const profileData = await fetchProfile(authUser.uid, authUser);
          setProfile(profileData);
          
          // Persisted session return: record once per browser tab (sessionStorage) so Admin Analytics shows activity beyond fresh password logins.
          try {
            if (
              typeof sessionStorage !== 'undefined' &&
              !sessionStorage.getItem('ysr_web_session_resume_logged')
            ) {
              const { recordWebLoginEvent } = await import('@/lib/recordWebLoginEvent');
              const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
              await recordWebLoginEvent(authUser, path, { eventType: 'session_resume' });
              sessionStorage.setItem('ysr_web_session_resume_logged', '1');
            }
          } catch (_) { /* non-blocking */ }
          
          // Push notifications removed - service workers disabled
        } else {
          console.log('❌ Firebase Auth: No user authenticated');
          setUser(null);
          setProfile(null);
          
        }
        
        // CRITICAL: Set these immediately after handling auth state, don't wait for push notifications
        setAuthInitialized(true);
        setLoading(false);
      });

      return () => unsubscribe();
    };

    initializeAuth();

  }, [auth, fetchProfile, setUserState]);

  const signUp = useCallback(async (email, password, options = {}) => {
    console.log('🚀 Firebase SignUp called from:', window.location.pathname);
    console.log('🚀 SignUp options:', options);
    
    if (!auth) {
      console.error('❌ Firebase Auth not available');
      toast({
        variant: "destructive",
        title: "Sign up Failed",
        description: "Firebase authentication is not available. Please refresh the page.",
      });
      return { error: new Error('Firebase Auth not available') };
    }
    
    // Extract artist name from different possible structures
    const artistName = options?.artist_name || 
                      options?.data?.artist_name || 
                      options?.data?.artistName || 
                      'New Artist';
    
    // Extract full name (REQUIRED for Stripe account creation)
    const fullName = options?.fullName || 
                     options?.data?.fullName || 
                     null;
    
    // Extract URL slug if provided
    const urlSlug = options?.url_slug || 
                   options?.data?.url_slug || 
                   options?.data?.profileUrl || 
                   null;
    
    // Extract date of birth (REQUIRED for Stripe account creation)
    const dateOfBirth = options?.dateOfBirth || 
                       options?.data?.dateOfBirth || 
                       null;
                       
    const isAgent = options?.data?.isAgent || false;
    const payoutPreference = options?.data?.payoutPreference || '80_20';
    const agentCode = options?.data?.agentCode || null;
    
    const accountType = detectAccountType(artistName, email, window.location.pathname, isAgent);
    console.log('🚀 SignUp extracted data:', { artistName, fullName, urlSlug, dateOfBirth, accountType });

    if (urlSlug && String(urlSlug).trim() !== '' && isQrPromoReservedSlug(urlSlug)) {
      toast({
        variant: 'destructive',
        title: 'Invalid profile URL',
        description: QR_SLUG_RESERVED_MESSAGE,
      });
      return { error: new Error(QR_SLUG_RESERVED_MESSAGE) };
    }

    const signupPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    const isKaraokeRoute = signupPath.includes('/karaoke');
    const isAgentSignup = isAgent || signupPath.includes('/agent/signup');
    const rawCountry = options?.data?.country ?? options?.country ?? '';
    const trimmedCountry = String(rawCountry).trim();

    if (!isKaraokeRoute && !trimmedCountry) {
      toast({
        variant: 'destructive',
        title: 'Country required',
        description: isAgentSignup
          ? 'Please select United Kingdom or United States.'
          : 'Please select your country of residence. It sets your currency and payment region.',
      });
      return { error: new Error('Country of residence is required.') };
    }

    if (isAgentSignup && trimmedCountry) {
      const agentCc = trimmedCountry.toUpperCase().slice(0, 2);
      if (agentCc !== 'GB' && agentCc !== 'US') {
        toast({
          variant: 'destructive',
          title: 'Invalid region',
          description: 'Agent accounts are only available for the United Kingdom or United States.',
        });
        return { error: new Error('Agent signup requires country GB or US.') };
      }
    }
    
    // Note: Full Name and Date of Birth are collected but not required for signup
    // We'll try to create Stripe account on signup, but if it fails, we'll try again when going Live
    
    try {
      console.log('🚀 SignUp: Creating user with Firebase...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('ysr_web_session_resume_logged', '1');
      }
      console.log('🚀 SignUp: User created successfully:', newUser.uid);
      
      // Import currency utilities
      let detectedCountry = 'GB';
      let getCurrencyForCountry = null;
      try {
        const currencyModule = await import('@/lib/currency');
        const { detectCountry, getCurrencyForCountry: getCurrency } = currencyModule;
        getCurrencyForCountry = getCurrency;

        if (isKaraokeRoute && !trimmedCountry) {
          detectedCountry = detectCountry();
          console.log('🌍 SignUp: Karaoke route — auto-detected country from timezone:', detectedCountry);
        } else if (trimmedCountry) {
          console.log('🌍 SignUp: Using artist-selected country:', trimmedCountry);
        }
      } catch (error) {
        console.warn('⚠️ SignUp: Currency detection failed, using defaults:', error);
      }
      
      const finalCountry = (trimmedCountry || detectedCountry || 'GB')
        .toString()
        .trim()
        .toUpperCase()
        .slice(0, 2);
      const finalCurrency = getCurrencyForCountry ? getCurrencyForCountry(finalCountry) : 'GBP';
      
      console.log('🌍 SignUp: Final currency:', finalCurrency, 'from country:', finalCountry);
      
      // Create profile in Firestore
      const profileData = {
        artist_name: artistName,
        account_type: accountType,
        email: newUser.email,
        created_at: new Date().toISOString(),
        currency: finalCurrency, // Currency based on SELECTED country, not detected
        country: finalCountry, // Use country from signup form (2-letter ISO code)
      };
      
      // Add full name if provided
      if (fullName) {
        profileData.full_name = fullName.trim();
      }
      
      // Add date of birth if provided
      if (dateOfBirth) {
        profileData.date_of_birth = dateOfBirth;
      }
      
      // Add URL slug if provided (store in lowercase for case-insensitive matching)
      if (urlSlug) {
        profileData.url_slug = urlSlug.trim().toLowerCase();
        profileData.karaoke_name = artistName; // For karaoke accounts, also set karaoke_name
      }
      
      let newAgentCode = null;
      if (isAgent) {
        const generateAgentCode = () => {
          const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
          let result = '';
          for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        };
        
        newAgentCode = generateAgentCode();
        profileData.agent_code = newAgentCode;
        profileData.default_payout_preference = payoutPreference;
      } else if (agentCode) {
        // Link new artist to their agent
        try {
          const agentCodeDoc = await getDoc(doc(db, 'agent_codes', agentCode.toUpperCase()));
          if (agentCodeDoc.exists()) {
            const agentId = agentCodeDoc.data().agent_id;
            profileData.agent_id = agentId;
            
            // Also fetch the agent's default payout preference to apply it to this artist
            const agentProfileDoc = await getDoc(doc(db, 'profiles', agentId));
            if (agentProfileDoc.exists() && agentProfileDoc.data().default_payout_preference) {
              profileData.agent_payout_preference = agentProfileDoc.data().default_payout_preference;
            } else {
              profileData.agent_payout_preference = '80_20'; // Fallback
            }
            console.log(`🔗 SignUp: Artist linked to Agent ${agentId} with preference ${profileData.agent_payout_preference}`);
          } else {
            console.warn(`⚠️ SignUp: Agent code ${agentCode} not found in database`);
          }
        } catch (agentErr) {
          console.error("Error looking up agent code:", agentErr);
        }
      }
      
      console.log('🚀 SignUp: Creating profile in Firestore...');
      debugLog('REFERRAL_SIGNUP', 'Creating user profile in Firestore', { userId: newUser.uid, accountType, artistName });
      await setDoc(doc(db, 'profiles', newUser.uid), profileData);
      console.log('🚀 SignUp: Profile created successfully');
      debugLog('SUCCESS', 'User profile created successfully', { userId: newUser.uid, accountType });
      
      if (isAgent && newAgentCode) {
        console.log('🚀 SignUp: Creating agent code mapping...');
        const agentCodeRef = doc(db, 'agent_codes', newAgentCode);
        await setDoc(agentCodeRef, {
          agent_id: newUser.uid,
          created_at: new Date().toISOString()
        });
      }
      
      // Process referral code if present
      await processReferralCode(newUser.uid);
      
      // Create URL slug mapping if URL slug was provided (use lowercase)
      if (urlSlug) {
        console.log('🚀 SignUp: Creating URL slug mapping...');
        const urlSlugRef = doc(db, 'url_slugs', urlSlug.trim().toLowerCase());
        await setDoc(urlSlugRef, {
          user_id: newUser.uid,
          account_type: accountType,
          created_at: new Date(),
          updated_at: new Date()
        });
        console.log('🚀 SignUp: URL slug mapping created');
      }
      
      // Only show toast on desktop
      if (!window.innerWidth || window.innerWidth > 768) {
        toast({
          title: "Account Created Successfully!",
          description: "Welcome! Setting up your dashboard...",
        });
      }
      
      // Mark just signed up for one-time onboarding
      console.log('🚀 SignUp: Setting onboarding flag...');
      try {
        localStorage.setItem('ysr_just_signed_up', 'true');
        console.log('🎯 SignUp: Set ysr_just_signed_up flag for onboarding');
      } catch (e) {
        console.warn('Unable to set signup flag in localStorage', e);
      }

      // Push notifications removed - service workers disabled // Immediately invoked async function - runs in background

      console.log('✅ Manual payout mode active - skipping Stripe Connect onboarding setup on signup');

      // Only redirect if user is on a login/signup page
      const currentPath = window.location.pathname;
      console.log('🚀 SignUp: Current path:', currentPath);
      if (currentPath.includes('/login') || currentPath.includes('/signup')) {
        let redirectPath = '/admin';
        if (accountType === 'karaoke') {
          redirectPath = '/karaoke/admin';
        } else if (accountType === 'agent') {
          redirectPath = '/agent/dashboard';
        }
        
        console.log('🚀 SignUp: Redirecting to:', redirectPath);
        
        // Use setTimeout to ensure navigation happens after all state updates
        setTimeout(() => {
          try {
            navigate(redirectPath);
          } catch (navError) {
            console.error('❌ Navigation error, using window.location:', navError);
            // Fallback to window.location if navigate fails
            window.location.href = redirectPath;
          }
        }, 100);
      }
      // If user is already on a karaoke page, don't redirect them
      
      console.log('🚀 SignUp: Returning success');
      return { data: userCredential };
    } catch (error) {
      console.error('❌ Firebase SignUp error:', error);
      console.error('❌ SignUp error details:', error.message, error.code);
      toast({
        variant: "destructive",
        title: "Sign up Failed",
        description: error.message || "Something went wrong",
      });
      return { error };
    }
  }, [auth, toast, navigate]);

  // Process referral code after user creation
  const processReferralCode = useCallback(async (userId) => {
    try {
      console.log(`🔍 DEBUG: processReferralCode called with userId: ${userId}`);
      console.log(`🔍 DEBUG: processReferral function available:`, typeof processReferral);
      
      // Check for referral code in URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlReferralCode = urlParams.get('ref');
      
      // Check for referral code in localStorage (from signup form)
      const localStorageReferralCode = localStorage.getItem('pendingReferralCode');
      
      const referralCode = urlReferralCode || localStorageReferralCode;
      
      if (referralCode) {
        console.log('🔍 Processing referral code:', referralCode);
        debugLog('REFERRAL_SIGNUP', `Processing referral code: ${referralCode}`, { referralCode, userId });
        
        // Add a small delay to ensure user profile is fully created
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Process the referral with retry logic
        let success = false;
        let attempts = 0;
        const maxAttempts = 3;
        
        while (!success && attempts < maxAttempts) {
          attempts++;
          console.log(`🔄 Referral processing attempt ${attempts}/${maxAttempts}`);
          
          try {
            console.log(`🔍 DEBUG: About to call processReferral function`);
            console.log(`🔍 DEBUG: processReferral function exists:`, typeof processReferral);
            console.log(`🔍 DEBUG: Calling processReferral with code: ${referralCode}, userId: ${userId}`);
            
            // Test if we can call the function at all
            if (typeof processReferral !== 'function') {
              console.error(`❌ processReferral is not a function, it's:`, typeof processReferral);
              throw new Error('processReferral function not available');
            }
            
            success = await processReferral(referralCode, userId);
            console.log(`🔍 DEBUG: processReferral returned: ${success}`);
            if (success) {
              console.log('✅ Referral processed successfully');
              debugLog('SUCCESS', 'Referral processed successfully', { referralCode, userId, attempts });
              // Clear the pending referral code from localStorage
              localStorage.removeItem('pendingReferralCode');
            } else {
              console.log(`❌ Referral processing failed on attempt ${attempts} - processReferral returned false`);
              if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
              }
            }
          } catch (retryError) {
            console.error(`❌ Referral processing error on attempt ${attempts}:`, retryError);
            console.error(`❌ Error details:`, retryError.message, retryError.code);
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
            }
          }
        }
        
        if (!success) {
          console.log('❌ Failed to process referral code after all attempts');
          debugLog('ERROR', 'Failed to process referral code after all attempts', { referralCode, userId, attempts });
        }
      } else {
        console.log('ℹ️ No referral code found');
        debugLog('INFO', 'No referral code found during signup');
      }
    } catch (error) {
      console.error('❌ Error processing referral code:', error);
      debugLog('ERROR', 'Critical error in referral processing', { error: error.message });
      // Don't throw error - referral processing failure shouldn't break signup
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    if (!auth) {
      console.error('❌ Firebase Auth not available');
      toast({
        variant: "destructive",
        title: "Sign in Failed",
        description: "Firebase authentication is not available. Please refresh the page.",
      });
      return { error: new Error('Firebase Auth not available') };
    }

    try {
      console.log('🔐 Attempting to sign in with email:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const signedInUser = userCredential.user;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('ysr_web_session_resume_logged', '1');
      }
      console.log('✅ Sign in successful:', signedInUser.uid);

      try {
        const { recordWebLoginEvent } = await import('@/lib/recordWebLoginEvent');
        const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
        await recordWebLoginEvent(signedInUser, path, { eventType: 'password_login' });
      } catch (_) { /* non-blocking */ }
      
      // Get user profile to determine account type with timeout to prevent hangs
      let userProfile = null;
      let accountType = 'artist';
      try {
        const profilePromise = fetchProfile(signedInUser.uid, signedInUser);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
        );
        userProfile = await Promise.race([profilePromise, timeoutPromise]);
        accountType = userProfile?.account_type || 'artist';
      } catch (profileError) {
        console.warn('⚠️ Profile fetch failed or timed out (non-critical):', profileError.message);
        // Continue with default account type
        accountType = 'artist';
      }
      
      
      // Push notifications removed - service workers disabled
      
      // Only show toast on desktop
      if (!window.innerWidth || window.innerWidth > 768) {
        toast({
          title: "Signed in successfully!",
          description: "Welcome back!",
        });
      }
      
      // Determine redirect based on current context and account type
      const currentPath = window.location.pathname;
      let redirectPath;
      
      // Only redirect if user is on a login page
      if (currentPath.includes('/login')) {
        if (currentPath.includes('/karaoke')) {
          // User signed up/signed in from karaoke login page
          redirectPath = '/karaoke/admin';
        } else if (accountType === 'agent') {
          redirectPath = '/agent/dashboard';
        } else {
          // User signed up/signed in from artist login page
          redirectPath = '/admin';
        }
        // Use window.location for more reliable navigation
        try {
          navigate(redirectPath);
        } catch (navError) {
          console.warn('⚠️ Navigate failed, using window.location:', navError);
          window.location.href = redirectPath;
        }
      }
      // If user is already on a karaoke page (not login), don't redirect them
      // Let them stay where they are
      
      return { data: userCredential };
    } catch (error) {
      console.error('❌ Firebase SignIn error:', error);
      console.error('❌ SignIn error code:', error.code);
      console.error('❌ SignIn error message:', error.message);
      
      let errorMessage = "Something went wrong";
      if (error.code === 'auth/user-not-found') {
        errorMessage = "No account found with this email address.";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address.";
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = "Invalid email or password.";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        variant: "destructive",
        title: "Sign in Failed",
        description: errorMessage,
      });
      return { error };
    }
  }, [auth, toast, navigate, fetchProfile]);


  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      
      // Clear ALL stored session data
      localStorage.removeItem('ysr_user_logged_in');
      localStorage.removeItem('ysr_user_uid');
      localStorage.removeItem('ysr_user_email');
      localStorage.removeItem('ysr_user_session');
      sessionStorage.removeItem('ysr_web_session_resume_logged');
      
      
      // Redirect based on current location
      const currentPath = window.location.pathname;
      const redirectPath = currentPath.includes('/karaoke') ? '/karaoke/login' : '/';
      navigate(redirectPath);
      
      return {};
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Sign out Failed",
        description: error.message || "Something went wrong",
      });
      return { error };
    }
  }, [auth, toast, navigate]);

  // Check for logout URL parameter and trigger logout if present
  useEffect(() => {
    if (!authInitialized) return; // Wait for auth to initialize
    
    const urlParams = new URLSearchParams(window.location.search);
    const logoutParam = urlParams.get('logout');
    
    if (logoutParam === '1' || logoutParam === 'true') {
      console.log('🚪 Logout URL parameter detected - signing out...');
      
      // Clean up the URL parameter immediately to prevent loops
      const url = new URL(window.location.href);
      url.searchParams.delete('logout');
      window.history.replaceState({}, '', url.toString());
      
      // Trigger logout if user is logged in, or just redirect if not
      if (user) {
        signOut();
      } else {
        // No user logged in, just redirect to appropriate page
        const currentPath = window.location.pathname;
        const redirectPath = currentPath.includes('/karaoke') ? '/karaoke/login' : '/';
        navigate(redirectPath);
      }
    }
  }, [authInitialized, user, signOut, navigate]);

  const updateProfileData = useCallback(async (userId, newData) => {
    const profileDocRef = doc(db, 'profiles', userId);
    try {
      await updateDoc(profileDocRef, {
        ...newData,
        updated_at: new Date().toISOString()
      });
      const updatedProfile = await fetchProfile(userId);
      setProfile(updatedProfile);
    } catch(error) {
      console.error("Error updating profile:", error);
    }
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const updatedProfile = await fetchProfile(user.uid);
      setProfile(updatedProfile);
    } catch(error) {
      console.error("Error refreshing profile:", error);
    }
  }, [user?.uid, fetchProfile]);

  // Manual auth refresh
  const refreshAuth = useCallback(async () => {
    if (!auth) return;
    
    console.log('🔄 Manually refreshing auth state...');
    setLoading(true);
    setAuthInitialized(false);
    
    try {
      // Force a reload of the current user if exists
      if (auth.currentUser) {
        await auth.currentUser.reload();
        console.log('✅ Manual auth refresh: User reloaded:', auth.currentUser.uid);
        setUser(auth.currentUser);
        const profileData = await fetchProfile(auth.currentUser.uid, auth.currentUser);
        setProfile(profileData);
      } else {
        console.log('❌ Manual auth refresh: No current user found');
        setUser(null);
        setProfile(null);
      }
    } catch (error) {
      console.error('❌ Manual auth refresh error:', error);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
      setAuthInitialized(true);
    }
  }, [auth, fetchProfile]);



  const value = useMemo(() => ({
    user,
    profile,
    loading,
    authInitialized,
    signUp,
    signIn,
    signOut,
    updateProfileData,
    refreshProfile,
    refreshAuth
  }), [user, profile, loading, authInitialized, signUp, signIn, signOut, updateProfileData, refreshProfile, refreshAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Instead of throwing an error, log it and return null values
    console.warn('useAuth called outside AuthProvider context');
    
    // Return default values to prevent app crashes
    return {
      user: null,
      profile: null,
      loading: true,
      authInitialized: false,
      signUp: async () => {},
      signIn: async () => {},
      signOut: async () => {},
      updateProfileData: async () => {},
      refreshProfile: async () => {},
      refreshAuth: async () => {}
    };
  }
  return context;
};